import { Component, OnInit, Input, ElementRef, ViewChild, ChangeDetectorRef, HostListener, OnDestroy } from '@angular/core';
import { DriverIdentificationService } from '../../services/driver-identification.service';
import { MultimediaStoreService } from 'src/app/multiview/services/multimedia-store.service';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { DriverIdentificationData } from '../../models/driver-identification.model';

@Component({
  selector: 'app-driver-selection-list',
  templateUrl: './driver-selection-list.component.html',
  styleUrls: ['./driver-selection-list.component.scss']
})
export class DriverSelectionListComponent implements OnInit, OnDestroy {
  @Input() displayOnPanelFn!: () => void;
  @ViewChild('pantalla') pantalla!: ElementRef;
  @ViewChild('driverCarousel') driverCarousel!: ElementRef;
  @ViewChild('verticalCarousel') verticalCarousel!: ElementRef;

  private subscription: Subscription = new Subscription();
  currentDrivers: DriverIdentificationData[] = [];
  isFocused = false;

  public driverIdentificationData$ = this.driverIdentificationService.driverIdentificationData$;


  driverFromEventName$ = this.driverIdentificationData$.pipe(
    map(drivers => drivers.find(d => d.fromEvent)?.name ?? 'No identificado')
  );

  // filters
  selectedFilter: 'all' | 'checklist' | 'enrolled' | 'evaluated' = 'all';
  public filter$ = new BehaviorSubject<'all' | 'checklist' | 'enrolled' | 'evaluated'>('all');

  constructor(
    public driverIdentificationService: DriverIdentificationService,
    public multimediaStoreService: MultimediaStoreService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement) {
        this.pantalla.nativeElement.classList.add('oculto');
      }
    });

    this.subscription.add(
      this.filteredDrivers$.subscribe(drivers => {
        this.currentDrivers = drivers;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // filters and counts
  withChecklistCount$ = this.driverIdentificationData$.pipe(
    map(list => list?.filter(d => d.withChecklist).length || 0)
  );
  enrolledCount$ = this.driverIdentificationData$.pipe(
    map(list => list?.filter(d => d.enrolled).length || 0)
  );
  evaluatedCount$ = this.driverIdentificationData$.pipe(
    map(list => list?.filter(d => d.evaluated).length || 0)
  );
  totalCount$ = this.driverIdentificationData$.pipe(
    map(list => list?.length || 0)
  );
  filteredDrivers$ = combineLatest([
    this.driverIdentificationData$,
    this.filter$
  ]).pipe(
    map(([list, filter]) => {
      if (!list) return [];

      switch (filter) {
        case 'checklist':
          return [...list]
            .filter(d => d.withChecklist)
            .sort((a, b) => {
              const parseDate = (date?: string) => {
                if (!date || date === '-') return 0;

                const [datePart, timePart] = date.split(', ');

                if (!datePart || !timePart) return 0;

                const [day, month, year] = datePart.split('/').map(Number);
                const [hours, minutes, seconds] = timePart.split(':').map(Number);

                const timestamp = new Date(
                  year,
                  month - 1,
                  day,
                  hours,
                  minutes,
                  seconds
                ).getTime();

                return isNaN(timestamp) ? 0 : timestamp;
              };

              return parseDate(b.checklistDate) - parseDate(a.checklistDate);
            });
        case 'enrolled':
          return list
            .filter(d => d.enrolled)
            .sort((a, b) =>
              a.name.trim().localeCompare(
                b.name.trim(),
                'es',
                { sensitivity: 'base' }
              )
            );
        case 'evaluated':
          return [...list]
            .filter(d => d.evaluated)
            .sort((a, b) => {
              const parseDate = (date?: string) => {
                if (!date || date === '-') return 0;

                const [datePart, timePart] = date.split(', ');

                if (!datePart || !timePart) return 0;

                const [day, month, year] = datePart.split('/').map(Number);
                const [hours, minutes, seconds] = timePart.split(':').map(Number);

                const timestamp = new Date(
                  year,
                  month - 1,
                  day,
                  hours,
                  minutes,
                  seconds
                ).getTime();

                return isNaN(timestamp) ? 0 : timestamp;
              };

              return parseDate(b.lastSeen) - parseDate(a.lastSeen);
            });

        default:
          return list;
      }
    })
  );
  setFilter(filter: 'all' | 'checklist' | 'enrolled' | 'evaluated') {
    this.filter$.next(filter);
  }

  getDriverDisplayDate(driver: DriverIdentificationData): string {
    if (!driver) return '';
    if (this.filter$.value === 'evaluated') {
      return driver.lastSeen && driver.lastSeen !== '-' ? driver.lastSeen : '';
    }
    return driver.checklistDate && driver.checklistDate !== '-' ? driver.checklistDate : '';
  }

  // events user
  public onToggleDisplayOnPanel() {
    this.displayOnPanelFn();
  }

  public onClosePanel() {
    this.driverIdentificationService.closeDriverIdentificationPanel();
  }

  // event test
  multimedias: any[] = [];
  selectedMediaIndex = 0;
  selectedDriverId: string | null = null;

  onActivateFullscreen(driverId? : string) {

    this.multimedias = this.multimediaStoreService
      .getMultimediasByUuid(this.driverIdentificationService.eventUuid || "") || [];
    this.selectedMediaIndex = 0;

    if(!driverId){
      const serviceDriverId = this.driverIdentificationService.driverIdSubject.value;
      if (serviceDriverId) {
        this.selectedDriverId = serviceDriverId;
      } else {
        let lastDriver = this.driverIdentificationService.getDriversValue()[0];
        this.selectedDriverId = lastDriver.id || null
      }
    }else {
      this.selectedDriverId = driverId || null;
    }

    const elem = this.pantalla.nativeElement;
    elem.classList.remove('oculto');

    this.cdr.detectChanges();
    setTimeout(() => {
      this.scrollToSelectedDriver();
    }, 200);


    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) { // Safari
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) { // IE
      elem.msRequestFullscreen();
    }
  }

  getSelectedDriver(drivers: DriverIdentificationData[]): DriverIdentificationData | null {
    if (!drivers || drivers.length === 0) return null;

    return this.selectedDriverId
      ? drivers.find(d => d.id === this.selectedDriverId) || drivers[0]
      : drivers[0];
  }

  onSelectDriverId(driverId: string) {
    this.selectedDriverId = driverId;
    this.cdr.detectChanges();
    this.scrollToSelectedDriver();
  }

  private scrollToSelectedDriver() {
    if (this.driverCarousel) {
      const selectedElem = this.driverCarousel.nativeElement.querySelector('.selected');
      if (selectedElem) {
        selectedElem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }

  onCloseFullscreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen();
      // Pequeño delay para que se actualice la vista al salir de fullscreen
      setTimeout(() => {
        this.scrollToSelectedVertical();
      }, 100);
      this.isFocused = true;
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!document.fullscreenElement && !this.isFocused) return;

    // Si el foco está en un campo de texto, no aplicar los atajos
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || (target as any).isContentEditable) {
      return;
    }

    if (document.fullscreenElement) {
      if (event.key === 'ArrowRight') {
        this.navigateDrivers(1);
      } else if (event.key === 'ArrowLeft') {
        this.navigateDrivers(-1);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault(); // Evitar scroll con espacio
        const selected = this.getSelectedDriver(this.currentDrivers);
        if (selected) {
          this.onDriverDblClick(selected);
        }
      }
    } else {
      // Navegación vertical
      if (event.key === 'ArrowDown') {
        this.navigateVertical(1);
      } else if (event.key === 'ArrowUp') {
        this.navigateVertical(-1);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault(); // Evitar scroll con espacio
        this.onActivateFullscreen();
      }
    }
  }

  private navigateVertical(direction: number) {
    if (this.currentDrivers.length === 0) return;

    const currentId = this.driverIdentificationService.driverIdSubject.value;
    const currentIndex = this.currentDrivers.findIndex(d => d.id === currentId);
    let nextIndex = currentIndex + direction;

    if (nextIndex >= this.currentDrivers.length) nextIndex = 0;
    if (nextIndex < 0) nextIndex = this.currentDrivers.length - 1;

    const nextDriver = this.currentDrivers[nextIndex];
    if (this.driverIdentificationService.driverIdSubject.value !== nextDriver.id) {
      this.driverIdentificationService.selectDriver(nextDriver);
    }

    this.cdr.detectChanges();
    this.scrollToSelectedVertical();
  }

  private scrollToSelectedVertical() {
    if (this.verticalCarousel) {
      // Buscamos el elemento que tenga la clase .selected (que viene del componente hijo app-driver-selection-item)
      const selectedElem = this.verticalCarousel.nativeElement.querySelector('.selected');
      if (selectedElem) {
        selectedElem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }

  private navigateDrivers(direction: number) {
    if (this.currentDrivers.length === 0) return;

    // Si no hay conductor seleccionado, empezamos por el primero o el último según la dirección
    if (!this.selectedDriverId) {
      const nextIndex = direction > 0 ? 0 : this.currentDrivers.length - 1;
      this.onSelectDriverId(this.currentDrivers[nextIndex].id);
      return;
    }

    const currentIndex = this.currentDrivers.findIndex(d => d.id === this.selectedDriverId);
    let nextIndex = currentIndex + direction;

    if (nextIndex >= this.currentDrivers.length) nextIndex = 0;
    if (nextIndex < 0) nextIndex = this.currentDrivers.length - 1;

    this.onSelectDriverId(this.currentDrivers[nextIndex].id);
  }

  onDriverDblClick(driver : DriverIdentificationData) {
    if(this.driverIdentificationService.driverIdSubject.value != driver.id) {
      this.driverIdentificationService.selectDriver(driver);
    }
    this.onCloseFullscreen();
  }
}
